import React, { useState, useEffect } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  Modal,
  ModalHeader,
  ModalBody,
  Spinner,
} from 'reactstrap';
import { diaryService } from '../services';
import { EventType } from '../types';
import BreadCrumb from '../components/Common/BreadCrumb';
import { toast } from 'react-toastify';

const EventTypesPage: React.FC = () => {
  const [eventTypes, setEventTypes] = useState<EventType[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingEventType, setEditingEventType] = useState<EventType | null>(null);
  const [form, setForm] = useState({ name: '', color: '' });
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleteModal, setDeleteModal] = useState<{ show: boolean; eventTypeId: string }>({ show: false, eventTypeId: '' });

  useEffect(() => {
    loadEventTypes();
  }, []);

  const loadEventTypes = async () => {
    setLoading(true);
    try {
      const data = await diaryService.getEventTypes();
      setEventTypes(Array.isArray(data) ? data : []);
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to load event types');
    } finally {
      setLoading(false);
    }
  };

  const openAdd = () => {
    setEditingEventType(null);
    setForm({ name: '', color: '' });
    setModalOpen(true);
  };

  const openEdit = (eventType: EventType) => {
    setEditingEventType(eventType);
    setForm({ name: eventType.name, color: eventType.color || '' });
    setModalOpen(true);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error('Event type name is required');
      return;
    }
    setSaving(true);
    try {
      const color = form.color?.trim() || '#5A62D1';
      if (editingEventType) {
        await diaryService.updateEventType(editingEventType._id, { ...form, color });
        toast.success('Event type updated');
      } else {
        await diaryService.createEventType({ name: form.name.trim(), color });
        toast.success('Event type created');
      }
      setModalOpen(false);
      loadEventTypes();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to save event type');
    } finally {
      setSaving(false);
    }
  };

  const openDeleteModal = (id: string) => {
    setDeleteModal({ show: true, eventTypeId: id });
  };

  const handleDelete = async () => {
    if (!deleteModal.eventTypeId) return;
    setDeleteId(deleteModal.eventTypeId);
    try {
      await diaryService.deleteEventType(deleteModal.eventTypeId);
      toast.success('Event type deleted');
      loadEventTypes();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to delete event type');
    } finally {
      setDeleteId(null);
      setDeleteModal({ show: false, eventTypeId: '' });
    }
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="Event Types" pageTitle="Diary Management" />
          <Row>
            <Col xs={12}>
              <Card>
                <CardBody>
                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <h5 className="card-title mb-0">
                      <i className="ri-shapes-line me-2"></i>
                      Event Types
                    </h5>
                    <Button color="primary" onClick={openAdd}>
                      <i className="ri-add-line me-1"></i>
                      Add Event Type
                    </Button>
                  </div>
                  <p className="text-muted small mb-4">
                    Manage event types used for calendar events (e.g. Online Workshop). Each type has a name and color.
                  </p>
                  {loading ? (
                    <div className="text-center py-5">
                      <Spinner color="primary" />
                      <p className="mt-2 text-muted">Loading event types...</p>
                    </div>
                  ) : eventTypes.length === 0 ? (
                    <div className="text-center py-5 border rounded bg-light">
                      <i className="ri-shapes-line display-4 text-muted"></i>
                      <p className="mt-2 mb-2">No event types yet</p>
                      <Button color="primary" onClick={openAdd}>
                        <i className="ri-add-line me-1"></i>
                        Add First Event Type
                      </Button>
                    </div>
                  ) : (
                    <div className="d-flex flex-wrap gap-2">
                      {eventTypes.map((eventType) => (
                        <div
                          key={eventType._id}
                          className="d-flex align-items-center gap-2 rounded border p-2"
                          style={{ backgroundColor: '#f8f9fa' }}
                        >
                          <span
                            className="rounded-circle"
                            style={{
                              width: 14,
                              height: 14,
                              backgroundColor: eventType.color || '#9CA3AF',
                            }}
                          />
                          <span className="fw-medium">{eventType.name}</span>
                          <Button
                            color="light"
                            size="sm"
                            className="ms-1"
                            onClick={() => openEdit(eventType)}
                          >
                            <i className="ri-edit-2-line"></i>
                          </Button>
                          <Button
                            color="danger"
                            size="sm"
                            outline
                            disabled={deleteId === eventType._id}
                            onClick={() => openDeleteModal(eventType._id)}
                          >
                            {deleteId === eventType._id ? (
                              <Spinner size="sm" />
                            ) : (
                              <i className="ri-delete-bin-line"></i>
                            )}
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      <Modal isOpen={modalOpen} toggle={() => setModalOpen(false)}>
        <ModalHeader toggle={() => setModalOpen(false)}>
          {editingEventType ? 'Edit Event Type' : 'Add Event Type'}
        </ModalHeader>
        <ModalBody>
          <Form onSubmit={handleSave}>
            <FormGroup>
              <Label>Name <span className="text-danger">*</span></Label>
              <Input
                type="text"
                placeholder="e.g. Online Workshop"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                maxLength={100}
                required
              />
            </FormGroup>
            <FormGroup>
              <Label>Color</Label>
              <div className="d-flex align-items-center gap-2">
                <Input
                  type="color"
                  value={form.color ? (form.color.startsWith('#') ? form.color : `#${form.color}`) : '#5A62D1'}
                  onChange={(e) => setForm((f) => ({ ...f, color: e.target.value }))}
                  style={{ width: 50, height: 38 }}
                />
                <Input
                  type="text"
                  value={form.color}
                  onChange={(e) => setForm((f) => ({ ...f, color: e.target.value }))}
                  placeholder="#5A62D1"
                />
              </div>
            </FormGroup>
            <div className="d-flex gap-2 justify-content-end mt-3">
              <Button type="button" color="light" onClick={() => setModalOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" color="primary" disabled={saving}>
                {saving ? <Spinner size="sm" className="me-2" /> : null}
                {editingEventType ? 'Update' : 'Create'}
              </Button>
            </div>
          </Form>
        </ModalBody>
      </Modal>

      <Modal isOpen={deleteModal.show} toggle={() => setDeleteModal({ show: false, eventTypeId: '' })} centered>
        <ModalHeader toggle={() => setDeleteModal({ show: false, eventTypeId: '' })}>
          Confirm Delete
        </ModalHeader>
        <ModalBody>
          <p className="mb-3">Are you sure you want to delete this event type?</p>
          <div className="d-flex justify-content-end gap-2">
            <Button
              type="button"
              color="light"
              onClick={() => setDeleteModal({ show: false, eventTypeId: '' })}
              disabled={Boolean(deleteId)}
            >
              Cancel
            </Button>
            <Button type="button" color="danger" onClick={handleDelete} disabled={Boolean(deleteId)}>
              {deleteId ? <Spinner size="sm" className="me-2" /> : null}
              Delete
            </Button>
          </div>
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
};

export default EventTypesPage;
